import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api, type PhoneCall, type PhoneCallTranscription } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import { absoluteTime } from "../../lib/time";
import { formatPhone } from "../crm/PhoneField";
import { cn } from "../ui/cn";
import { callDuration, directionLabel } from "./call-format";

/**
 * One call: what the phone system recorded about it, who it was with, and --
 * for somebody allowed to hear it -- the recording and the transcript.
 *
 * The recording and the transcript are behind `canHear`, which the server
 * decides (admins, and the agent who handled the call) and checks again on
 * the routes themselves. This component only decides what to *offer*: a
 * player nobody may use is a control that only ever fails.
 *
 * Both are fetched from the phone system when the call is opened, never
 * before. The transcript is the content of a conversation, and reading every
 * one on the page into the browser just because the list was scrolled past
 * would be the copy the server deliberately does not keep.
 */
export function CallDetail({
  callId,
  seed,
  onClose,
}: {
  callId: number;
  /** The list's copy of the row, so the pane paints before the fetch lands. */
  seed: PhoneCall | undefined;
  onClose: () => void;
}) {
  const call = useQuery({
    queryKey: queryKeys.phoneCall(callId),
    queryFn: () => api.phoneCall(callId),
    placeholderData: seed,
    enabled: Number.isFinite(callId),
  });

  const row = call.data;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="flex shrink-0 items-center gap-2 border-b border-line px-4 py-2">
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-content">
          {row ? partyName(row) : "Call"}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-faint hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400"
        >
          ×
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">
        {!row ? (
          <p className="py-8 text-center text-sm text-faint">
            {call.isLoading ? "Loading…" : "That call is not here."}
          </p>
        ) : (
          <Body call={row} />
        )}
      </div>
    </div>
  );
}

function Body({ call }: { call: PhoneCall }) {
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-xs">
        <Term>When</Term>
        <dd className="text-content">{absoluteTime(call.startedAt)}</dd>

        <Term>Direction</Term>
        <dd className={cn("text-content", call.missed && "font-medium text-red-600 dark:text-red-400")}>
          {directionLabel(call)}
          {/* The phone system's own word, when it says more than answered or
              not -- "busy", "voicemail". Yay spells the ordinary miss
              "noanswer", which the direction label already says. */}
          {call.disposition &&
          !["answered", "noanswer", "no answer"].includes(call.disposition) ? (
            <span className="ml-1 text-faint">({call.disposition})</span>
          ) : null}
        </dd>

        <Term>Duration</Term>
        <dd className="tabular-nums text-content">{callDuration(call)}</dd>

        {call.remote ? (
          <>
            <Term>{call.direction === "inbound" ? "From" : "To"}</Term>
            <dd className="text-content">
              <RemoteParty remote={call.remote} />
            </dd>
          </>
        ) : null}

        {call.line ? (
          <>
            <Term>Line</Term>
            <dd className="text-content">{formatPhone(call.line)}</dd>
          </>
        ) : null}

        <Term>Agent</Term>
        <dd className="text-content">
          {call.agent ? (
            <>
              {call.agent.name ?? "Unnamed"}
              {call.agent.extension ? (
                <span className="ml-1 text-faint">ext. {call.agent.extension}</span>
              ) : null}
              {!call.agent.linked ? (
                <p className="mt-0.5 text-[11px] text-faint">
                  Not linked to a Pulse user. Choose {call.agent.name ?? "them"}{" "}
                  as somebody's phone user in Settings → Users and this call,
                  and every one before it, will be theirs.
                </p>
              ) : null}
            </>
          ) : (
            /* A missed call to a line, which rang the line rather than a
               person -- see `sidesOf` on the server. */
            <span className="text-faint">Nobody — it rang the line</span>
          )}
        </dd>
      </dl>

      <Recording call={call} />
      <Transcript call={call} />
    </div>
  );
}

function Term({ children }: { children: React.ReactNode }) {
  return <dt className="text-faint">{children}</dt>;
}

/** Who the other end was, as far as the CRM can tell from their number. */
function RemoteParty({ remote }: { remote: NonNullable<PhoneCall["remote"]> }) {
  const number = formatPhone(remote.number);

  return (
    <span>
      {remote.contact ? (
        <span className="font-medium">{remote.contact.name ?? "Unnamed contact"}</span>
      ) : null}
      {remote.company ? (
        <>
          {remote.contact ? <span className="text-faint"> at </span> : null}
          <Link
            to={`/crm/companies/${remote.company.id}`}
            className="text-brand-600 hover:underline dark:text-brand-400"
          >
            {remote.company.name}
          </Link>
        </>
      ) : null}
      <span className={cn("block", (remote.contact || remote.company) && "text-faint")}>
        {number}
        {remote.area ? <span className="text-faint"> · {remote.area}</span> : null}
      </span>
      {!remote.contact && !remote.company ? (
        <span className="block text-[11px] text-faint">
          Not a number the CRM knows.
        </span>
      ) : null}
    </span>
  );
}

function Recording({ call }: { call: PhoneCall }) {
  const [failed, setFailed] = useState(false);

  if (!call.hasRecording) {
    return <p className="text-xs text-faint">This call was not recorded.</p>;
  }

  if (!call.canHear) {
    return (
      <p className="rounded-md bg-surface-2 px-3 py-2 text-xs text-muted-on-tint">
        Recorded. Recordings are for admins and the agent who handled the call.
      </p>
    );
  }

  return (
    <section>
      <h3 className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-faint">
        Recording
      </h3>
      {failed ? (
        /*
          An `<audio>` element reports a refused or missing source as one
          opaque error, with no status to read, so both likely causes are
          named: retention, where the phone system has deleted the recording
          and the row outlives it, and the phone system's rate limit, which a
          second try a moment later gets past.
        */
        <p className="text-xs text-faint">
          Could not play this recording. The phone system may be busy — try
          again in a moment — or may no longer keep recordings this old.{" "}
          <button
            type="button"
            onClick={() => setFailed(false)}
            className="text-brand-600 hover:underline dark:text-brand-400"
          >
            Try again
          </button>
        </p>
      ) : (
        /* `preload="none"`: asking for the URL is what fetches a short-lived
           link from the phone system, and that should happen when somebody
           presses play rather than every time the pane opens. Keyed on the
           call so switching calls never plays the previous one. */
        <audio
          key={call.id}
          controls
          preload="none"
          src={api.phoneCallRecordingUrl(call.id)}
          onError={() => setFailed(true)}
          className="w-full"
        />
      )}
    </section>
  );
}

function Transcript({ call }: { call: PhoneCall }) {
  const status = call.transcriptionStatus;
  const wanted = call.canHear && status === "completed";

  const transcript = useQuery({
    queryKey: queryKeys.phoneCallTranscription(call.id),
    queryFn: () => api.phoneCallTranscription(call.id),
    enabled: wanted,
    /* A finished transcript does not change. */
    staleTime: Infinity,
  });

  if (!call.canHear || !status) return null;

  if (status !== "completed") {
    return (
      <p className="text-xs text-faint">
        {status === "failed"
          ? "The phone system could not transcribe this call."
          : "The transcript is not ready yet."}
      </p>
    );
  }

  return (
    <section>
      <h3 className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-faint">
        Transcript
      </h3>
      {transcript.isLoading ? (
        <p className="text-xs text-faint">Loading…</p>
      ) : transcript.isError ? (
        <p className="text-xs text-faint">The transcript could not be read.</p>
      ) : transcript.data ? (
        <TranscriptBody transcript={transcript.data} />
      ) : (
        <p className="text-xs text-faint">The phone system has no transcript for this call.</p>
      )}
    </section>
  );
}

function TranscriptBody({ transcript }: { transcript: PhoneCallTranscription }) {
  return (
    <div className="space-y-3">
      {transcript.summary ? (
        <div className="rounded-md bg-surface-2 px-3 py-2 text-xs text-content">
          {/* Labelled as the phone system's, because it is a machine's
              reading of the call and should not be mistaken for somebody's
              notes. */}
          <p className="mb-1 text-[11px] text-faint">Summary, by the phone system</p>
          <p className="whitespace-pre-line">{transcript.summary}</p>
        </div>
      ) : null}

      {transcript.actionItems.length > 0 ? (
        <div>
          <p className="mb-1 text-[11px] text-faint">Action items</p>
          <ul className="list-disc space-y-0.5 pl-4 text-xs text-content">
            {transcript.actionItems.map((item, index) => (
              <li key={index}>
                {item.description}
                {item.owner || item.dueDate ? (
                  <span className="text-faint">
                    {" "}
                    — {[item.owner, item.dueDate].filter(Boolean).join(", ")}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {transcript.utterances.length > 0 ? (
        <ol className="space-y-1.5 text-xs">
          {transcript.utterances.map((line, index) => (
            <li key={index} className="grid grid-cols-[3rem_1fr] gap-2">
              <span className="pt-px tabular-nums text-faint">
                {line.start === null ? "" : clock(line.start)}
              </span>
              <span>
                {line.speaker ? (
                  <span className="mr-1 font-medium text-muted">
                    {speakerLabel(line.speaker)}:
                  </span>
                ) : null}
                <span className="text-content">{line.text}</span>
              </span>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}

/** The heading's name for a call: who it was with, as specifically as known. */
export function partyName(call: PhoneCall): string {
  if (!call.remote) return call.agent?.name ? `Internal — ${call.agent.name}` : "Internal call";
  return (
    call.remote.contact?.name ??
    call.remote.company?.name ??
    formatPhone(call.remote.number)
  );
}

/** Seconds into the recording, as m:ss. */
function clock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

/** "caller" to "Caller"; anything else as Yay sent it. */
function speakerLabel(speaker: string): string {
  return speaker.charAt(0).toUpperCase() + speaker.slice(1);
}
